import React, { useCallback, useEffect, useMemo, useState, lazy, Suspense } from "react";
import { NavLink, Navigate, useNavigate, useLocation, Routes, Route } from "react-router-dom";
import {
  Loader2, ShieldCheck, Check, X, RefreshCw, LogOut, ShieldOff,
  FileSearch, BarChart3, History, LifeBuoy, Copy, Trash2,
  KeyRound, Microscope, Archive as ArchiveBox, ArrowRight, FolderOpen, Radio, Cpu,
  Link2, BookOpen, EyeOff, IdCard,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  adminMe, adminListLeads, adminApproveAsLead, adminInvestigate,
  adminArchiveSubmission, adminDecide,
  adminLock, adminAuditLog,
  adminRecoveryGenerate, adminRecoveryList, adminRecoveryRevoke,
  adminRbacMe,
} from "../lib/api";
import { AnalyticsPanel } from "../components/admin/AnalyticsPanel";
import { AdminCommentsPanel } from "../components/admin/AdminCommentsPanel";
import { TotpSecurityPanel } from "../components/admin/TotpSecurityPanel";
import { AudienceAnalyticsPanel } from "../components/admin/AudienceAnalyticsPanel";
import { LiveAudiencePanel } from "../components/admin/LiveAudiencePanel";
import { AdminReviewFolder } from "../components/admin/AdminReviewFolder";
import { AdminHeroReview } from "../components/admin/AdminHeroReview";
import { AdminSourceHealth } from "../components/admin/AdminSourceHealth";
import { AdminOracleCertification } from "../components/admin/AdminOracleCertification";
import { AdminMediaLibrary } from "../components/admin/AdminMediaLibrary";
import { AdminEditorialDashboard } from "../components/admin/AdminEditorialDashboard";
import { OracleSecurityEvents } from "../components/admin/OracleSecurityEvents";
import { AdminProfileReviewQueue } from "../components/admin/AdminProfileReviewQueue";
import { AdminNetworkHealth } from "../components/admin/AdminNetworkHealth";
import { AdminNewsRadar } from "../components/admin/AdminNewsRadar";
import { AdminAutomation } from "../components/admin/AdminAutomation";
import { AdminSourcesPanel } from "../components/admin/AdminSourcesPanel";
import { AdminEpstein } from "../components/admin/AdminEpstein";
import { AdminDossierEditor } from "../components/admin/AdminDossierEditor";
import { AdminDossierPullback } from "../components/admin/AdminDossierPullback";
import { AdminAssetDirectory } from "../components/admin/AdminAssetDirectory";
import { AdminSettingsPanel } from "../components/admin/AdminSettingsPanel";
import { AdminUsersPanel } from "../components/admin/AdminUsersPanel";
import { AdminJerichoConsole } from "../components/admin/AdminJerichoConsole";
import { AdminSecurityFailedAccess } from "../components/admin/AdminSecurityFailedAccess";
import { AdminPrivacyPanel } from "../components/admin/AdminPrivacyPanel";
import { AdminDispatchModeration } from "../components/admin/AdminDispatchModeration";
import { AdminOperationsShell } from "../components/admin/AdminOperationsShell";
import { AdminIdleLockGuard } from "../components/admin/AdminIdleLockGuard";
import { SecurityCenter } from "../components/admin/SecurityCenter";
import { OracleHistory } from "../components/admin/OracleHistory";
import { CommandCenter } from "../components/admin/CommandCenter";
import { EmailHub } from "../components/admin/EmailHub";
import { MemberDirectory } from "../components/admin/MemberDirectory";
import { LEGACY_REDIRECTS } from "../components/admin/adminSections";
import AdminInvestigation from "./AdminInvestigation";

const OP_TOKEN_KEY = "archive-admin-token";

// -------- helpers --------
const Score = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">{label}</span>
    <span className="font-display text-xl text-[#E4DFD3]">{value ?? "—"}<span className="text-[#757065] text-xs">/10</span></span>
  </div>
);

const RiskPill = ({ label, value }) => {
  const color = value === "high" || value === "likely"
    ? "text-[#D92D20] border-[#D92D20]/50"
    : value === "medium" || value === "possible"
      ? "text-[#CBB892] border-[#CBB892]/40"
      : "text-[#5DD4B3] border-[#5DD4B3]/30";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 border font-mono text-[9px] uppercase tracking-[0.25em] ${color}`}>
      {label}: {value || "—"}
    </span>
  );
};

const STATUS_META = {
  pending_review:  { label: "PENDING REVIEW",   color: "#CBB892" },
  approved_lead:   { label: "APPROVED LEAD",    color: "#5DD4B3" },
  investigating:   { label: "INVESTIGATING…",   color: "#E0A526" },
  draft_generated: { label: "DRAFT READY",      color: "#9FA8D4" },
  published:       { label: "PUBLISHED",        color: "#5DD4B3" },
  rejected:        { label: "REJECTED",         color: "#D92D20" },
  archived:        { label: "ARCHIVED",         color: "#757065" },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending_review;
  return (
    <span
      style={{ color: m.color, borderColor: m.color }}
      className="inline-flex items-center px-2 py-0.5 border font-mono text-[9px] uppercase tracking-[0.25em]"
    >
      {m.label}
    </span>
  );
};

const LeadCard = ({ sub, opToken, userToken, onChange }) => {
  const r = sub.ai_report || {};
  const ds = sub.draft_summary;
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");
  const ws = sub.workflow_status;
  const isSpammy = Array.isArray(sub.spam_flags) && sub.spam_flags.length > 0;

  const wrap = (fn) => async (...args) => {
    if (busy) return;
    setBusy(true);
    try { await fn(...args); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const approveLead = wrap(async () => {
    await adminApproveAsLead(opToken, sub.id, "", userToken);
    toast.success("Saved as a lead");
  });

  const investigate = wrap(async (force = false) => {
    if (isSpammy && !force) {
      if (!window.confirm("This submission is flagged as spam. Investigate anyway? (uses LLM credit)")) return;
      const r2 = await adminInvestigate(opToken, sub.id, { force: true }, userToken);
      toast.success("Investigation started — moved to Review Folder · Investigating", {
        action: { label: "Open case file", onClick: () => nav(`/admin/investigation/${r2.draft_id}`) },
        duration: 7000,
      });
      return;
    }
    const r2 = await adminInvestigate(opToken, sub.id, { force }, userToken);
    toast.success("Investigation started — moved to Review Folder · Investigating", {
      action: { label: "Open case file", onClick: () => nav(`/admin/investigation/${r2.draft_id}`) },
      duration: 7000,
    });
  });

  const reject = wrap(async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    await adminDecide(opToken, sub.id, "reject", reason.trim(), userToken);
    toast.success("Rejected");
  });

  const archive = wrap(async () => {
    if (!window.confirm("Archive this submission?")) return;
    await adminArchiveSubmission(opToken, sub.id, "", userToken);
    toast.success("Archived");
  });

  const openDraft = () => {
    if (ds?.id) nav(`/admin/investigation/${ds.id}`);
  };
  const [showFull, setShowFull] = useState(false);

  return (
    <div className="border border-[#2D2A26] bg-[#131313] p-6 aged-paper" data-testid={`lead-card-${sub.id}`}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <StatusBadge status={ws} />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">CAT: {sub.category}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065]">
          {new Date(sub.created_at).toLocaleDateString()} · {sub.submitter || "anonymous"}
        </span>
        {ds && (
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9FA8D4]">
            draft: {ds.status} · {ds.source_count} src · conf {ds.confidence}
          </span>
        )}
      </div>
      <h3 className="font-heading text-2xl text-[#E4DFD3] mb-2">{sub.title}</h3>
      <p className="text-sm text-[#A39E93] leading-relaxed mb-2 line-clamp-3">{sub.rationale}</p>
      <button onClick={() => setShowFull((v) => !v)} data-testid={`lead-full-record-btn-${sub.id}`}
        className="mb-4 font-mono text-[9px] uppercase tracking-[0.25em] text-[#CBB892] border border-[#CBB892]/40 px-2 py-1">
        {showFull ? "Hide full record" : "View full record"}
      </button>
      {showFull && (
        <div className="mb-4 border border-[#3A3630] bg-black/40 p-4 space-y-3"
             data-testid={`lead-full-record-${sub.id}`}>
          <div>
            <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#757065] mb-1">Full original title</div>
            <div className="text-sm text-[#E4DFD3]">{sub.title}</div>
          </div>
          <div>
            <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#757065] mb-1">Complete original message</div>
            <div className="text-sm text-[#A39E93] leading-relaxed whitespace-pre-wrap">{sub.rationale}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#757065] mb-1">Submitter</div>
              <div className="text-sm text-[#E4DFD3]">{sub.submitter || "anonymous"}</div>
            </div>
            <div>
              <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#757065] mb-1">Submitted</div>
              <div className="text-sm text-[#A39E93]">{new Date(sub.created_at).toLocaleString()}</div>
            </div>
          </div>
          {sub.rejection_reason && (
            <div>
              <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">Rejection reason</div>
              <div className="text-sm text-[#A39E93]">{sub.rejection_reason}</div>
            </div>
          )}
          {sub.ai_report && (
            <div>
              <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#9FA8D4] mb-1">AI assessment</div>
              <pre className="text-[11px] text-[#A39E93] whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">{JSON.stringify(sub.ai_report, null, 1)}</pre>
            </div>
          )}
          {(sub.spam_flags || []).length > 0 && (
            <div className="font-mono text-[9px] text-[#CBB892]">SPAM FLAGS: {sub.spam_flags.join(", ")}</div>
          )}
          <div>
            <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#757065] mb-1">Decision / audit history</div>
            {(sub.decision_log || []).length === 0 && (
              <div className="text-[11px] text-[#5A554C] font-mono">No decisions recorded</div>
            )}
            {(sub.decision_log || []).map((d, i) => (
              <div key={i} className="text-[11px] text-[#A39E93] font-mono border-l border-[#3A3630] pl-2 mb-1">
                {(d.at || "").slice(0, 19).replace("T", " ")} · {d.actor || "system"}
                {" "}({["spam_filter", "ai_screening", "ai", "system", "visibility-control"].includes((d.actor || "").toLowerCase()) ? "AUTOMATIC" : "HUMAN/ADMIN"})
                {" "}→ {d.action || d.decision}{d.note ? ` — ${d.note}` : ""}
              </div>
            ))}
          </div>
        </div>
      )}

      {(r.evidence_score || r.source_quality_score || r.controversy_score) && (
        <div className="grid grid-cols-3 gap-3 mb-4 border-t border-b border-[#2D2A26] py-3">
          <Score label="EVIDENCE" value={r.evidence_score} />
          <Score label="SOURCE QUALITY" value={r.source_quality_score} />
          <Score label="CONTROVERSY" value={r.controversy_score} />
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-4">
        {r.misinformation_risk && <RiskPill label="MISINFO" value={r.misinformation_risk} />}
        {r.duplicate_risk && <RiskPill label="DUPE" value={r.duplicate_risk} />}
        {ds?.duplicate_verdict && (
          <span className={`inline-flex items-center gap-1 px-2 py-1 border font-mono text-[9px] uppercase tracking-[0.25em] ${
            ds.duplicate_verdict === "novel" ? "text-[#5DD4B3] border-[#5DD4B3]/30"
              : ds.duplicate_verdict === "duplicate" ? "text-[#D92D20] border-[#D92D20]/50"
              : "text-[#CBB892] border-[#CBB892]/40"
          }`}>AI DUPE: {ds.duplicate_verdict}</span>
        )}
      </div>
      {isSpammy && (
        <div className="flex flex-wrap items-center gap-2 mb-4 border border-[#D92D20]/40 bg-[#D92D20]/5 px-3 py-2" data-testid={`spam-flags-${sub.id}`}>
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20]">// SPAM FLAGS</span>
          {sub.spam_flags.map((f) => (
            <span key={f} className="font-mono text-[9px] uppercase tracking-[0.25em] bg-[#1a0808] border border-[#D92D20]/60 text-[#FFA9A0] px-2 py-0.5">
              {f.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      )}
      <div className="border border-[#2D2A26] bg-[#0a0a0a] px-3 py-2 mb-4">
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">// TRACKING ID</span>
        <code className="ml-2 font-mono text-[10px] text-[#A39E93] break-all">{sub.id}</code>
      </div>

      {showReject ? (
        <div className="space-y-2">
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)}
            rows={2} placeholder="Rejection reason (required)…"
            data-testid={`reject-reason-${sub.id}`}
            className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-sm text-[#E4DFD3] placeholder:text-[#757065] resize-none"
          />
          <div className="flex gap-2">
            <button onClick={reject} disabled={busy} data-testid={`confirm-reject-${sub.id}`}
              className="flex-1 flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Confirm reject
            </button>
            <button onClick={() => { setShowReject(false); setReason(""); }}
              className="px-4 py-2 border border-[#454038] text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.25em]">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {/* Terminal states show "open draft" only */}
          {(ws === "draft_generated" || ws === "investigating" || ws === "published") && ds?.id && (
            <button onClick={openDraft} data-testid={`open-draft-${sub.id}`}
              className="flex-1 flex items-center justify-center gap-2 bg-[#9FA8D4]/15 hover:bg-[#9FA8D4]/25 border border-[#9FA8D4]/40 text-[#9FA8D4] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
              <FileSearch size={12} /> Open investigation <ArrowRight size={11} />
            </button>
          )}

          {(ws === "pending_review" || ws === "approved_lead") && (
            <>
              {ws !== "approved_lead" && (
                <button onClick={approveLead} disabled={busy} data-testid={`approve-lead-${sub.id}`}
                  className="flex items-center gap-2 border border-[#5DD4B3]/40 hover:bg-[#5DD4B3]/15 text-[#5DD4B3] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                  Approve as lead
                </button>
              )}
              <button onClick={() => investigate(false)} disabled={busy} data-testid={`investigate-${sub.id}`}
                className="flex-1 flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Microscope size={12} />}
                Investigate with AI
              </button>
              <button onClick={() => setShowReject(true)} data-testid={`reject-btn-${sub.id}`}
                className="flex items-center gap-2 border border-[#D92D20]/40 hover:bg-[#D92D20]/15 text-[#D92D20] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                <X size={12} /> Reject
              </button>
            </>
          )}

          {(ws !== "archived" && ws !== "rejected" && ws !== "published") && (
            <button onClick={archive} title="Archive submission"
              data-testid={`archive-${sub.id}`}
              className="flex items-center gap-1 border border-[#454038] hover:border-[#A39E93] text-[#757065] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
              <ArchiveBox size={12} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// -------- inner pages --------
const TABS = [
  { key: "pending_review",  label: "Pending" },
  { key: "approved_lead",   label: "Leads" },
  { key: "investigating",   label: "Investigating" },
  { key: "draft_generated", label: "Drafts" },
  { key: "published",       label: "Published" },
  { key: "rejected,archived", label: "Closed" },
];

const SubmissionsView = ({ opToken, userToken }) => {
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState("pending_review");
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListLeads(opToken,
        { workflow_status_filter: tab, limit: 200 }, userToken);
      setLeads(data.leads || []);
      setCounts(data.counts || {});
    } catch { toast.error("Failed to load editorial queue"); }
    finally { setLoading(false); }
  }, [opToken, userToken, tab]);
  useEffect(() => { load(); }, [load]);

  // Poll while any row is "investigating" so the badge flips to draft_generated.
  useEffect(() => {
    const any = leads.some((l) => l.workflow_status === "investigating");
    if (!any) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [leads, load]);

  return (
    <div data-testid="admin-submissions-view">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">// EDITORIAL DESK</div>
          <h1 className="font-display text-4xl text-[#E4DFD3]">Submissions &amp; Leads</h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            User submissions are leads, not public dossiers. Reject spam, save legitimate
            tips as leads, or run a full AI investigation — investigated cases land in the
            <NavLink to="/admin/review" className="text-[#D92D20] hover:underline mx-1" data-testid="editorial-desk-review-link">Review Folder</NavLink>
            as case files awaiting your sign-off. Nothing reaches the public archive
            without an explicit Publish action.
          </p>
        </div>
        <div className="flex gap-2">
          <NavLink to="/admin/review" data-testid="editorial-desk-open-review"
            className="flex items-center gap-2 border border-[#D92D20]/50 hover:bg-[#D92D20]/15 text-[#D92D20] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <FolderOpen size={12} /> Review folder
          </NavLink>
          <button onClick={load} data-testid="refresh-queue-btn"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 my-4 border-b border-[#2D2A26]">
        {TABS.map((t) => {
          const total = t.key.split(",").reduce((sum, k) => sum + (counts[k] || 0), 0);
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`leads-tab-${t.key}`}
              className={"px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 " +
                (active ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
              {t.label} {total ? <span className="ml-1 text-[#757065]">·&nbsp;{total}</span> : null}
            </button>
          );
        })}
      </div>

      {loading && leads.length === 0 ? (
        <p className="font-mono text-xs uppercase tracking-widest text-[#757065]">Loading…</p>
      ) : leads.length === 0 ? (
        <div className="border border-dashed border-[#2D2A26] p-16 text-center" data-testid="admin-queue-empty">
          <p className="font-mono text-xs uppercase tracking-widest text-[#757065]">Nothing in this view.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-queue">
          {leads.map((s) => (
            <LeadCard key={s.id} sub={s} opToken={opToken} userToken={userToken} onChange={load} />
          ))}
        </div>
      )}
    </div>
  );
};

const CommentsView = ({ opToken, userToken }) => (
  <div data-testid="admin-comments-view">
    <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">// COMMENT MODERATION</div>
    <h1 className="font-display text-4xl text-[#E4DFD3] mb-8">Reported Comments</h1>
    <AdminCommentsPanel token={opToken} userToken={userToken} />
  </div>
);

const AnalyticsView = ({ opToken, userToken }) => (
  <div data-testid="admin-analytics-view">
    <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">// AUDIENCE SIGNAL</div>
    <h1 className="font-display text-4xl text-[#E4DFD3] mb-8">Analytics</h1>
    <AnalyticsPanel token={opToken} userToken={userToken} />
  </div>
);

const AudienceView = ({ opToken, userToken }) => (
  <div data-testid="admin-audience-view">
    <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">// AUDIENCE & ENGAGEMENT</div>
    <h1 className="font-display text-4xl text-[#E4DFD3] mb-8">Audience</h1>
    <AudienceAnalyticsPanel token={opToken} userToken={userToken} />
  </div>
);

const LiveAudienceView = ({ opToken, userToken }) => (
  <div data-testid="admin-live-view">
    <LiveAudiencePanel token={opToken} userToken={userToken} />
  </div>
);

const GlobalReachConsole = lazy(() => import("../components/admin/globe/GlobalReachConsole"));

const GlobalReachView = ({ opToken, userToken }) => (
  <Suspense fallback={<div className="fixed inset-0 z-30 bg-black flex items-center justify-center text-[#A39E93] font-mono text-[10px] tracking-[0.3em] uppercase">// Booting intelligence console…</div>}>
    <div data-testid="admin-globe-view">
      <GlobalReachConsole token={opToken} userToken={userToken} />
    </div>
  </Suspense>
);

const ReviewFolderView = ({ opToken, userToken }) => (
  <AdminReviewFolder token={opToken} userToken={userToken} />
);

const NewsRadarView = ({ opToken, userToken }) => (
  <AdminNewsRadar token={opToken} userToken={userToken} />
);

const AutomationView = ({ opToken, userToken }) => (
  <AdminAutomation token={opToken} userToken={userToken} />
);

const SourcesView = ({ opToken, userToken }) => (
  <AdminSourcesPanel token={opToken} userToken={userToken} />
);

const EpsteinView = ({ opToken, userToken }) => (
  <AdminEpstein token={opToken} userToken={userToken} />
);
const DossierEditorView = ({ opToken, userToken }) => (
  <AdminDossierEditor token={opToken} userToken={userToken} />
);
const PullbackView = ({ opToken, userToken }) => (
  <AdminDossierPullback token={opToken} userToken={userToken} />
);
const AssetDirectoryView = ({ opToken, userToken }) => (
  <AdminAssetDirectory token={opToken} userToken={userToken} />
);
const SettingsView = ({ opToken, userToken }) => (
  <AdminSettingsPanel token={opToken} userToken={userToken} />
);
const UsersView = ({ opToken, userToken }) => (
  <AdminUsersPanel token={opToken} userToken={userToken} />
);
const EmailHubView = ({ opToken, userToken }) => (
  <EmailHub token={opToken} userToken={userToken} />
);
const MemberDirectoryView = ({ opToken, userToken }) => (
  <MemberDirectory token={opToken} userToken={userToken} />
);

const EVENT_LABELS = {
  admin_unlocked: "Admin unlocked (OTP verified)",
  admin_locked: "Admin locked",
  otp_sent: "OTP sent",
  otp_verify_failed: "OTP verification failed",
  otp_send_denied_not_allowlisted: "OTP send denied (not allowlisted)",
  admin_auth_denied: "Admin auth denied",
  submission_approved: "Submission approved & published",
  submission_rejected: "Submission rejected",
  comment_removed: "Comment removed",
  comment_restored: "Comment restored",
};

const AuditLogView = ({ opToken, userToken }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAuditLog(opToken, userToken, 200);
      setEvents(r.events || []);
    } catch { toast.error("Could not load audit log."); }
    finally { setLoading(false); }
  }, [opToken, userToken]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-audit-view">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">// AUDIT TRAIL</div>
          <h1 className="font-display text-4xl text-[#E4DFD3]">Recent admin activity</h1>
        </div>
        <button onClick={load} className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <p className="text-[#A39E93] mb-6">{events.length} event{events.length === 1 ? "" : "s"} recorded.</p>
      <div className="border border-[#2D2A26] bg-[#131313] divide-y divide-[#2D2A26]">
        {events.length === 0 && !loading ? (
          <div className="px-4 py-12 text-center font-mono text-xs uppercase tracking-widest text-[#757065]">
            No activity yet.
          </div>
        ) : events.map((e, i) => (
          <div key={i} className="px-4 py-3 grid grid-cols-12 gap-3 text-sm" data-testid={`audit-row-${i}`}>
            <div className="col-span-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#757065]">
              {new Date(e.ts).toLocaleString()}
            </div>
            <div className="col-span-4">
              <span className={"font-mono text-[10px] uppercase tracking-[0.25em] " + (e.success ? "text-[#5DD4B3]" : "text-[#D92D20]")}>
                {e.success ? "✓" : "✗"} {EVENT_LABELS[e.event] || e.event}
              </span>
            </div>
            <div className="col-span-3 font-mono text-xs text-[#A39E93]">{e.actor_email || "—"}</div>
            <div className="col-span-3 font-mono text-[10px] text-[#757065] truncate">
              {e.target ? <code>{e.target}</code> : (e.metadata && Object.keys(e.metadata).length ? Object.entries(e.metadata).slice(0,2).map(([k,v]) => `${k}=${v}`).join(" · ") : "")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// -------- 403 page (signed-in non-admin) --------
const NotAuthorizedView = ({ user, onSignOut }) => (
  <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#0a0907]" data-testid="admin-forbidden">
    <div className="w-full max-w-md border border-[#D92D20]/40 bg-[#131313] p-8 aged-paper">
      <div className="flex items-center gap-2 mb-4 text-[#D92D20]">
        <ShieldOff size={16} />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">// 403 — NOT AUTHORIZED</span>
      </div>
      <h1 className="font-display text-3xl text-[#E4DFD3] mb-3">Access denied</h1>
      <p className="text-sm text-[#A39E93] mb-6">
        Signed in as <span className="text-[#E4DFD3]">{user?.email || "(unknown)"}</span>. This
        account is not on the admin allowlist.
      </p>
      <div className="flex gap-2">
        <button
          onClick={onSignOut}
          data-testid="admin-signout-btn"
          className="flex-1 border border-[#454038] hover:border-[#D92D20] text-[#E4DFD3] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          Sign out
        </button>
        <a
          href="/"
          className="flex-1 text-center border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          Back to archive
        </a>
      </div>
    </div>
  </div>
);

const RecoveryView = ({ opToken, userToken }) => {
  const [unused, setUnused] = useState([]);
  const [usedCount, setUsedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminRecoveryList(opToken, userToken);
      setUnused(r.unused || []); setUsedCount(r.used_count || 0);
    } catch { toast.error("Could not load recovery codes."); }
    finally { setLoading(false); }
  }, [opToken, userToken]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (unused.length >= 5) {
      if (!window.confirm("You already have several active codes. Generate another?")) return;
    }
    try {
      const r = await adminRecoveryGenerate(opToken, userToken);
      setGenerated(r.code);
      load();
    } catch { toast.error("Could not generate code."); }
  };

  const revoke = async (prefix) => {
    if (!window.confirm("Revoke this recovery code permanently?")) return;
    try {
      await adminRecoveryRevoke(opToken, prefix, userToken);
      toast.success("Revoked");
      load();
    } catch { toast.error("Revoke failed."); }
  };

  const copyGenerated = async () => {
    try { await navigator.clipboard.writeText(generated); toast.success("Copied"); } catch { /* noop */ }
  };

  return (
    <div data-testid="admin-recovery-view">
      <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">// EMERGENCY ACCESS</div>
      <h1 className="font-display text-4xl text-[#E4DFD3] mb-3">Recovery codes</h1>
      <p className="text-[#A39E93] max-w-2xl mb-8 leading-relaxed">
        Single-use codes for when Google sign-in or email delivery is failing.
        Using one triggers a fresh 6-digit OTP to the admin email — recovery
        still requires OTP verification. Codes are stored hashed; the plaintext
        is shown ONCE at generation.
      </p>

      {generated && (
        <div className="border border-[#D92D20]/60 bg-[#1a0808] p-5 mb-6" data-testid="recovery-new-code">
          <div className="flex items-center gap-2 mb-2 text-[#D92D20]">
            <LifeBuoy size={14} />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em]">// NEW CODE — SHOWN ONCE</span>
          </div>
          <div className="flex items-center justify-between gap-3 bg-[#0a0a0a] border border-[#2D2A26] px-4 py-3 mb-3">
            <code className="font-mono text-lg tracking-[0.2em] text-[#E4DFD3]" data-testid="recovery-plaintext">{generated}</code>
            <button onClick={copyGenerated} className="flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]">
              <Copy size={11} /> Copy
            </button>
          </div>
          <p className="text-xs text-[#CBB892] mb-3">
            Store this code offline. It will not be shown again.
          </p>
          <button onClick={() => setGenerated(null)} className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] hover:text-[#E4DFD3]">
            I've saved it
          </button>
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <button onClick={generate} data-testid="generate-recovery-btn"
          className="flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
          <LifeBuoy size={11} /> Generate new code
        </button>
        <button onClick={load} className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.25em] text-[#757065]">
          {unused.length} active · {usedCount} used historically
        </span>
      </div>

      <div className="border border-[#2D2A26] bg-[#131313] divide-y divide-[#2D2A26]">
        {unused.length === 0 ? (
          <div className="px-4 py-10 text-center font-mono text-xs uppercase tracking-widest text-[#757065]">
            No active recovery codes. Generate one above and store it offline.
          </div>
        ) : unused.map((u, i) => (
          <div key={i} className="px-4 py-3 grid grid-cols-12 gap-3 text-sm items-center">
            <div className="col-span-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#757065]">
              {new Date(u.created_at).toLocaleString()}
            </div>
            <div className="col-span-4 font-mono text-xs text-[#A39E93]">
              Bound to {u.email || "(unknown)"}
            </div>
            <div className="col-span-3 font-mono text-[10px] text-[#757065]">
              <code className="text-[#A39E93]">{u.hash_prefix || "—"}</code> · by {u.created_by || "operator"}
            </div>
            <div className="col-span-2 text-right">
              <button onClick={() => revoke(u.hash_prefix)} className="inline-flex items-center gap-1.5 border border-[#D92D20]/40 hover:bg-[#D92D20]/15 text-[#D92D20] px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]">
                <Trash2 size={10} /> Revoke
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// -------- top-level Admin guard --------



// -------- top-level Admin guard --------
const Admin = () => {
  const { user, token: userToken, logout, loading: authLoading } = useAuth();
  const [opToken, setOpToken] = useState(() => sessionStorage.getItem(OP_TOKEN_KEY) || "");
  const [identity, setIdentity] = useState(null);
  const [state, setState] = useState("checking"); // checking | ok | needs-login | forbidden
  const [loginReason, setLoginReason] = useState(null);
  const nav = useNavigate();
  const loc = useLocation();

  const check = useCallback(async () => {
    if (authLoading) return;
    if (!opToken && !userToken) {
      // No credentials of any kind — go to login without firing an
      // unauthenticated /admin/me probe (those count as failed access
      // attempts on the security dashboard).
      setState("needs-login");
      return;
    }
    try {
      const me = await adminMe(opToken || null, userToken || null);
      if (me.is_admin) { setIdentity(me); setState("ok"); return; }
      // Allowlisted but needs OTP — bounce to login which will render OTP form.
      if (me.otp_required) { setState("needs-login"); return; }
      setState("needs-login");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) setState("forbidden");
      else if (code === 429) { toast.error("Rate-limited. Try again later."); setState("needs-login"); }
      else {
        // Held a credential but /admin/me rejected it — session expired.
        setLoginReason("session_expired");
        setState("needs-login");
      }
    }
  }, [opToken, userToken, authLoading]);

  // Recheck whenever the user/token/op-token changes (sign-in, sign-out, etc.)
  useEffect(() => { check(); }, [check]);

  // Redirect to /admin/login if needed
  useEffect(() => {
    if (state === "needs-login" && loc.pathname !== "/admin/login") {
      nav(
        loginReason ? `/admin/login?reason=${loginReason}` : "/admin/login",
        { state: { from: loc.pathname }, replace: true },
      );
    }
  }, [state, loc.pathname, nav, loginReason]);

  const signOut = useCallback(async () => {
    // Clear OTP-freshness server-side first (best-effort)
    try { if (userToken) await adminLock(userToken); } catch { /* noop */ }
    sessionStorage.removeItem(OP_TOKEN_KEY);
    setOpToken("");
    setIdentity(null);
    if (userToken) await logout();
    setState("needs-login");
    nav("/admin/login", { replace: true });
  }, [userToken, logout, nav]);

  // Idle-lock teardown: same shape as signOut but redirects with a reason so
  // AdminLogin can surface the "Session locked due to inactivity." banner.
  const idleLock = useCallback(async () => {
    try { if (userToken) await adminLock(userToken); } catch { /* noop */ }
    sessionStorage.removeItem(OP_TOKEN_KEY);
    setOpToken("");
    setIdentity(null);
    if (userToken) {
      try { await logout(); } catch { /* noop */ }
    }
    setState("needs-login");
    nav("/admin/login?reason=idle_locked", { replace: true });
  }, [userToken, logout, nav]);

  const childProps = useMemo(() => ({ opToken, userToken }), [opToken, userToken]);

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0907]" data-testid="admin-checking">
        <Loader2 size={22} className="animate-spin text-[#D92D20]" />
      </div>
    );
  }
  if (state === "forbidden") {
    return <NotAuthorizedView user={user} onSignOut={signOut} />;
  }
  if (state !== "ok") return null; // redirecting

  return (
    <AdminOperationsShell identity={identity} onSignOut={signOut} opToken={opToken} userToken={userToken}>
      <AdminIdleLockGuard opToken={opToken} userToken={userToken} onTimeout={idleLock}>
      <Routes>
        {/* === New Operations Center routing === */}
        <Route path="/" element={<Navigate to="/admin/command" replace />} />
        <Route path="/command" element={<CommandCenter {...childProps} />} />
        <Route path="/command/retrieval" element={<AdminOracleCertification token={opToken} userToken={userToken} />} />
        <Route path="/media" element={<AdminMediaLibrary token={opToken} userToken={userToken} />} />
        <Route path="/editorial" element={<AdminEditorialDashboard token={opToken} userToken={userToken} />} />

        {/* Dossiers section */}
        <Route path="/dossiers" element={<Navigate to="/admin/dossiers/submissions" replace />} />
        <Route path="/dossiers/submissions" element={<SubmissionsView {...childProps} />} />
        <Route path="/dossiers/review" element={<ReviewFolderView {...childProps} />} />
        <Route path="/dossiers/hero-review" element={<AdminHeroReview token={opToken} userToken={userToken} />} />
        <Route path="/dossiers/source-health" element={<AdminSourceHealth token={opToken} userToken={userToken} />} />
        <Route path="/dossiers/profile-review" element={<AdminProfileReviewQueue token={opToken} userToken={userToken} />} />
        <Route path="/dossiers/network-health" element={<AdminNetworkHealth token={opToken} userToken={userToken} />} />
        <Route path="/dossiers/news-radar" element={<NewsRadarView {...childProps} />} />
        <Route path="/dossiers/automation" element={<AutomationView {...childProps} />} />
        <Route path="/dossiers/epstein" element={<EpsteinView {...childProps} />} />
        <Route path="/dossiers/epstein/dossier/:slug" element={<DossierEditorView {...childProps} />} />
        <Route path="/dossiers/pullback" element={<PullbackView {...childProps} />} />
        <Route path="/dossiers/asset-directory" element={<AssetDirectoryView {...childProps} />} />
        <Route path="/dossiers/sources" element={<SourcesView {...childProps} />} />

        {/* Members section */}
        <Route path="/members" element={<Navigate to="/admin/members/audience" replace />} />
        <Route path="/members/audience" element={<MemberDirectoryView {...childProps} />} />
        <Route path="/members/live" element={<LiveAudienceView {...childProps} />} />
        <Route path="/members/reach" element={<GlobalReachView {...childProps} />} />
        <Route path="/members/comments" element={<CommentsView {...childProps} />} />
        <Route path="/members/analytics" element={<AnalyticsView {...childProps} />} />

        {/* Security section */}
        <Route path="/security" element={<Navigate to="/admin/security/center" replace />} />
        <Route path="/security/center" element={<SecurityCenter opToken={opToken} userToken={userToken} identity={identity} />} />
        <Route path="/security/oracle" element={<OracleHistory opToken={opToken} userToken={userToken} />} />
        <Route path="/security/oracle-events" element={<OracleSecurityEvents token={opToken} userToken={userToken} />} />
        <Route path="/security/failed-access" element={<AdminSecurityFailedAccess {...childProps} />} />
        <Route path="/security/audit" element={<AuditLogView {...childProps} />} />
        <Route path="/security/privacy" element={<AdminPrivacyPanel token={opToken} userToken={userToken} />} />
        <Route path="/security/dispatch" element={<AdminDispatchModeration token={opToken} userToken={userToken} />} />
        <Route path="/security/jericho" element={<AdminJerichoConsole {...childProps} />} />
        <Route path="/security/recovery" element={<RecoveryView {...childProps} />} />
        <Route path="/security/totp" element={<TotpSecurityPanel {...childProps} />} />

        {/* Settings section */}
        <Route path="/settings" element={<Navigate to="/admin/settings/site" replace />} />
        <Route path="/settings/site" element={<SettingsView {...childProps} />} />
        <Route path="/settings/users" element={<UsersView {...childProps} />} />
        <Route path="/settings/email" element={<EmailHubView {...childProps} />} />

        {/* Investigation deep-link (no section — modal-style page) */}
        <Route path="/investigation/:draftId" element={<AdminInvestigation />} />

        {/* === Legacy redirects (preserve bookmarks) === */}
        {LEGACY_REDIRECTS.map(([from, to]) => (
          <Route
            key={`legacy-${from}`}
            path={from.replace("/admin", "")}
            element={<Navigate to={to} replace />}
          />
        ))}

        {/* Catch-all → Command Center */}
        <Route path="*" element={<Navigate to="/admin/command" replace />} />
      </Routes>
      </AdminIdleLockGuard>
    </AdminOperationsShell>
  );
};

export default Admin;
